/**
 * Filtro compartido de Pricing: zona (ubigeo) y periodo.
 *
 * Encapsula el estado del filtro para que Dashboard y Estadísticas pidan
 * exactamente lo mismo a la API: departamento → provincia → distrito y,
 * opcionalmente, año y mes del histórico.
 */

import { useMemo, useState } from 'react';

import { idUbigeo, nombreUbigeo, useUbigeo, type Ubigeo } from '@/hooks/use-ubigeo';

import { nombreMes } from '../lib/periodos';
import type { FiltrosEstadisticas } from '../pricing.types';

export interface FiltrosUbicacion {
  ubigeo: Ubigeo;
  anio: string;
  mes: string;
  setAnio: (valor: string) => void;
  setMes: (valor: string) => void;
  /** Listo para pasar a `usePricingEstadisticas()`. */
  filtros: FiltrosEstadisticas;
  /** `LIMA / BARRANCA / BARRANCA` o `Todo el país`. */
  alcance: string;
  /** `Octubre 2026`, `2026` o vacío (todo el histórico). */
  periodo: string;
  hayFiltro: boolean;
  limpiar: () => void;
  /** Acceso rápido desde un chip de departamento con datos. */
  irADepartamento: (nombre: string) => void;
}

export function useFiltrosUbicacion(): FiltrosUbicacion {
  const ubigeo = useUbigeo();
  const [anio, setAnio] = useState('');
  const [mes, setMes] = useState('');

  const departamento = nombreUbigeo(ubigeo.departamentos, ubigeo.departamentoId);
  const provincia = nombreUbigeo(ubigeo.provincias, ubigeo.provinciaId);
  const distrito = nombreUbigeo(ubigeo.distritos, ubigeo.distritoId);

  const filtros = useMemo(
    () => ({ departamento, provincia, distrito, anio, mes }),
    [departamento, provincia, distrito, anio, mes],
  );

  const alcance = [departamento, provincia, distrito].filter(Boolean).join(' / ') || 'Todo el país';
  const periodo = anio ? [nombreMes(mes), anio].filter(Boolean).join(' ') : '';

  function limpiar() {
    ubigeo.limpiar();
    setAnio('');
    setMes('');
  }

  function irADepartamento(nombre: string) {
    const id = idUbigeo(ubigeo.departamentos, nombre);

    if (id) ubigeo.elegirDepartamento(id);
  }

  return {
    ubigeo,
    anio,
    mes,
    setAnio,
    setMes,
    filtros,
    alcance,
    periodo,
    hayFiltro: Boolean(departamento || anio),
    limpiar,
    irADepartamento,
  };
}
